import { Box, Button, Stack, Typography } from '@mui/material';
import { ArrowRight, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import RoiColor from '~/components/Structures/RoiColor';
import { isDeformableRegistration } from '~/domain/patient/deformableRegistration';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import {
    replanningRunOutputsState,
    replanningRunRegistrationsState,
    replanningRunSummaryState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import MiddleTruncated from './MiddleTruncated';
import { propagationModelLabelId } from './propagationModels';
import { useResetPropagation } from './useResetPropagation';

export default function RegisterRunSummary() {
    const intl = useIntl();
    const patient = useSelectedPatient();
    const runSummary = useRecoilValue(replanningRunSummaryState);
    const runRegistrations = useRecoilValue(replanningRunRegistrationsState);
    const { propagatedUid, contourFallbackNames = [] } = useRecoilValue(
        replanningRunOutputsState
    );
    const resultRois =
        patient?.structureSets.find((set) => set.dicomUid === propagatedUid)
            ?.roiArray ?? [];
    // Assigned to Contour+ but kept as propagated: the model did not produce them.
    const propagatedInstead = contourFallbackNames.map((name) => ({
        name,
        color: resultRois.find((roi) => roi.name.trim() === name.trim())?.color,
    }));
    const reset = useResetPropagation();
    const [confirming, setConfirming] = useState(false);
    const algorithmLabelId = propagationModelLabelId(runSummary?.modelName);

    const objects = [
        ...runRegistrations,
        ...(runSummary?.initRigidSopInstanceUid
            ? [runSummary.initRigidSopInstanceUid]
            : []),
    ].flatMap((sopInstanceUid) => {
        const registration =
            patient?.getRegistrationWithSopInstanceUid(sopInstanceUid);
        return registration
            ? [
                  {
                      sopInstanceUid,
                      label:
                          registration.contentLabel ||
                          intl.formatMessage({ id: 'registration' }),
                      labelId: isDeformableRegistration(registration)
                          ? 'replanning.summary.deformable'
                          : 'replanning.summary.rigid',
                  },
              ]
            : [];
    });

    return (
        <Stack spacing={1} px={1.5} pt={1.5} pb={1}>
            <Typography fontSize={11} data-testid="replanning-run-summary">
                <FormattedMessage
                    id="replanning.summary.performed"
                    values={{
                        algorithm: algorithmLabelId
                            ? intl.formatMessage({ id: algorithmLabelId })
                            : (runSummary?.modelName ?? ''),
                        propagated: runSummary?.propagatedCount ?? 0,
                        segmented: runSummary?.segmentedCount ?? 0,
                    }}
                />
            </Typography>

            {objects.map((object) => (
                <Box key={object.sopInstanceUid} minWidth={0}>
                    <Typography
                        fontSize={10}
                        color="text.secondary"
                        data-testid={`replanning-run-object-${object.sopInstanceUid}`}
                    >
                        <FormattedMessage id={object.labelId} />
                    </Typography>
                    <Typography fontSize={11}>{object.label}</Typography>
                    <Typography fontSize={10} color="text.secondary">
                        <MiddleTruncated value={object.sopInstanceUid} />
                    </Typography>
                </Box>
            ))}

            {propagatedInstead.length > 0 && (
                <Box data-testid="replanning-run-fallback">
                    <Typography fontSize={10} color="text.secondary">
                        <FormattedMessage id="replanning.summary.fallbackHeading" />
                    </Typography>
                    {propagatedInstead.map((roi) => (
                        <Box
                            key={roi.name}
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            minHeight={22}
                            data-testid={`replanning-run-fallback-${roi.name}`}
                        >
                            {roi.color && (
                                <RoiColor color={roi.color} size={9} />
                            )}
                            <Typography
                                fontSize={11}
                                flex={1}
                                minWidth={0}
                                noWrap
                                title={roi.name}
                            >
                                {roi.name}
                            </Typography>
                            <Typography fontSize={10} color="text.secondary">
                                <FormattedMessage id="replanning.rois.contour" />
                            </Typography>
                            <ArrowRight size={11} aria-hidden />
                            <Typography fontSize={10} color="warning.main">
                                <FormattedMessage id="replanning.rois.propagate" />
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            <Button
                size="small"
                variant="outlined"
                startIcon={<RotateCcw size={14} />}
                onClick={() => setConfirming(true)}
                data-testid="replanning-reset-registration"
                sx={{ fontSize: 11, alignSelf: 'flex-start' }}
            >
                <FormattedMessage id="replanning.summary.reset" />
            </Button>

            <ConfirmationDialog
                open={confirming}
                title={<FormattedMessage id="replanning.summary.resetTitle" />}
                message={
                    <FormattedMessage id="replanning.summary.resetMessage" />
                }
                onConfirm={() => {
                    setConfirming(false);
                    reset();
                }}
                onCancel={() => setConfirming(false)}
            />
        </Stack>
    );
}
